import React from 'react';
import { RecurrenceRule } from '../../types/tasks';
import { WEEKDAYS, WEEKDAY_FULL, recurrenceLabel } from '../../utils/recurrence';

// Repeat-rule picker, ported from the calendar app this feature is based on.
// Emits the whole rule on every change.
const MONTH_DAYS = Array.from({ length: 31 }, (_, i) => i + 1);

const RecurrencePicker: React.FC<{ value: RecurrenceRule; onChange: (rule: RecurrenceRule) => void }> = ({ value, onChange }) => {
  const type = value.type;
  const every = value.every ?? 7;
  const weekdays = value.weekdays ?? [];
  const monthDays = value.monthDays ?? [];

  const setType = (t: RecurrenceRule['type']) => onChange({ ...value, type: t, every, weekdays, monthDays });
  const toggle = (list: number[], n: number) =>
    list.includes(n) ? list.filter((x) => x !== n) : [...list, n].sort((a, b) => a - b);
  const preview = recurrenceLabel({ ...value, every, weekdays, monthDays });

  return (
    <div className="tk-recur">
      <div className="tk-segmented tk-segmented-sm" role="group" aria-label="Repeat type">
        <button type="button" className={type === 'weekdays' ? 'is-active' : ''} onClick={() => setType('weekdays')}>Days of week</button>
        <button type="button" className={type === 'monthdays' ? 'is-active' : ''} onClick={() => setType('monthdays')}>Days of month</button>
        <button type="button" className={type === 'days' ? 'is-active' : ''} onClick={() => setType('days')}>Every X days</button>
      </div>

      {type === 'weekdays' && (
        <div className="tk-recur-days">
          {WEEKDAYS.map((label, i) => (
            <button
              key={label}
              type="button"
              className={`tk-recur-day${weekdays.includes(i + 1) ? ' is-active' : ''}`}
              onClick={() => onChange({ ...value, weekdays: toggle(weekdays, i + 1) })}
              title={WEEKDAY_FULL[i]}
              aria-pressed={weekdays.includes(i + 1)}
            >
              {label.charAt(0)}
            </button>
          ))}
        </div>
      )}

      {type === 'monthdays' && (
        <div className="tk-recur-month">
          {MONTH_DAYS.map((d) => (
            <button
              key={d}
              type="button"
              className={`tk-recur-day tk-recur-mday${monthDays.includes(d) ? ' is-active' : ''}`}
              onClick={() => onChange({ ...value, monthDays: toggle(monthDays, d) })}
              aria-pressed={monthDays.includes(d)}
            >
              {d}
            </button>
          ))}
        </div>
      )}

      {type === 'days' && (
        <div className="tk-recur-every">
          <span>Every</span>
          <input
            type="number"
            min={1}
            max={365}
            className="tk-input tk-input-num"
            value={every}
            onChange={(e) => onChange({ ...value, every: Math.max(1, parseInt(e.target.value, 10) || 1) })}
          />
          <span>days</span>
        </div>
      )}

      {preview && <div className="tk-recur-preview">{preview}</div>}
    </div>
  );
};

export default RecurrencePicker;
